import { resolvePagesDataStoreKey } from '@/common/utils/resolvePageStoreEntry'
import {
  extractActionScriptText,
  extractActionTextValue,
} from '@/services/action/normalizeActionTransfer'
import {
  resolveActionLayoutLevelText,
  resolveActionLayoutTextId,
} from '@/services/action/actionScriptRuntime'

type ParsedHotKey = {
  ctrl: boolean
  alt: boolean
  shift: boolean
  meta: boolean
  key: string
}

type HotKeyCandidate = {
  context: any
  parsed: ParsedHotKey
}

type UseHotKeysParams = {
  actions: { value: Record<string, any> }
  appStore: any
  currentPlatform: { value: string | null | undefined }
  findPageDataByFormId: (formId: string) => any
  formProvider: any
  formStates: any
  getActionDialogClosedScript: (actionData: Record<string, any> | null | undefined) => string | undefined
  getCurrentFormId: () => string
  getCurrentRegistryFormId: () => string
  getForm: (params: any) => any
  getPageData: (params: any) => any
  getTableSelection: (formId?: string | number, panelId?: string | number, elementId?: string | number) => any
  getTableSelections: (formId?: string | number, panelId?: string | number) => any
  onAction: (params: { getContext: () => any }) => Promise<any> | any
  reloadElementViaControls: (elementName: string, data: any, actionContext?: any) => Promise<any> | any
  router: any
  runCode: (cmd: string, selectedData?: any) => Promise<any> | any
  tempActions: any
}

const HOTKEY_MODIFIER_TOKENS: Record<string, keyof Omit<ParsedHotKey, 'key'>> = {
  ctrl: 'ctrl',
  control: 'ctrl',
  shift: 'shift',
  alt: 'alt',
  option: 'alt',
  meta: 'meta',
  cmd: 'meta',
  command: 'meta',
}

const normalizeHotKeyToken = (token: string): string => token.trim().toLowerCase()

const normalizeKeyboardEventKey = (key: string): string => {
  if (!key) return ''
  if (key === ' ') return 'space'
  return key.trim().toLowerCase()
}

const normalizeKeyboardEventCode = (code: string): string => {
  if (!code) return ''
  const normalized = code.trim()
  if (!normalized) return ''
  if (normalized.startsWith('Key') && normalized.length === 4) {
    return normalized.slice(3).toLowerCase()
  }
  if (normalized.startsWith('Digit') && normalized.length === 6) {
    return normalized.slice(5)
  }
  if (normalized === 'Space') return 'space'
  return normalized.toLowerCase()
}

const parseHotKey = (rawHotKey: unknown): ParsedHotKey | null => {
  if (rawHotKey == null) return null
  const text = String(rawHotKey).trim()
  if (!text) return null

  const tokens = text
    .split(/\s+|\+/g)
    .map((token) => token.trim())
    .filter(Boolean)

  if (tokens.length === 0) return null

  const parsed: ParsedHotKey = {
    ctrl: false,
    alt: false,
    shift: false,
    meta: false,
    key: '',
  }

  for (const token of tokens) {
    const normalizedToken = normalizeHotKeyToken(token)
    const modifier = HOTKEY_MODIFIER_TOKENS[normalizedToken]
    if (modifier) {
      parsed[modifier] = true
      continue
    }
    parsed.key = normalizedToken
  }

  return parsed.key ? parsed : null
}

const doesHotKeyMatch = (event: KeyboardEvent, hotKey: ParsedHotKey): boolean => {
  const eventKey = normalizeKeyboardEventKey(event.key)
  const eventCode = normalizeKeyboardEventCode(event.code)
  const keyMatches = eventKey === hotKey.key || eventCode === hotKey.key
  return (
    event.ctrlKey === hotKey.ctrl &&
    event.altKey === hotKey.alt &&
    event.shiftKey === hotKey.shift &&
    event.metaKey === hotKey.meta &&
    keyMatches
  )
}

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName.toUpperCase()
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

const resolveActionHotKeyText = (actionData: any): string => {
  const rawHotKey = actionData?.hotKey
  if (Array.isArray(rawHotKey)) {
    return rawHotKey
      .map((entry) => String(entry?._text ?? entry ?? '').trim())
      .find(Boolean) || ''
  }
  return String(rawHotKey?._text ?? rawHotKey ?? '').trim()
}

export function useHotKeys(params: UseHotKeysParams) {
  const {
    actions,
    appStore,
    currentPlatform,
    findPageDataByFormId,
    formProvider,
    formStates,
    getActionDialogClosedScript,
    getCurrentFormId,
    getCurrentRegistryFormId,
    getForm,
    getPageData,
    getTableSelection,
    getTableSelections,
    onAction,
    reloadElementViaControls,
    router,
    runCode,
    tempActions,
  } = params

  const collectHotKeyCandidates = (): HotKeyCandidate[] => {
    const currentRegistryFormId = String(getCurrentRegistryFormId() || '')
    const currentSlotFormId = String(getCurrentFormId() || '')
    if (!currentRegistryFormId && !currentSlotFormId) return []

    const candidatesAction = new Map<string, HotKeyCandidate>()
    const registeredActions = Object.values(actions.value || {})
    const registeredById = new Map<string, any>()
    for (const registeredAction of registeredActions) {
      const rawAction = registeredAction?.data
      if (!rawAction) continue
      const formId = String(registeredAction?.formId ?? '')
      if (
        formId &&
        formId !== currentRegistryFormId &&
        formId !== currentSlotFormId
      ) {
        continue
      }

      const hotKeyText = resolveActionHotKeyText(rawAction)
      const parsed = parseHotKey(hotKeyText)
      if (!parsed) continue

      const context = registeredAction
      const actionId = String(
        registeredAction?.id ??
        rawAction?._attributes?.id ??
        rawAction?.id ??
        '',
      )
      const key =
        actionId ||
        `${context?.level || ''}:${context?.elementId || context?.panelId || context?.pageId || context?.name || hotKeyText}`
      if (!candidatesAction.has(key)) {
        candidatesAction.set(key, { context, parsed })
      }
      if (actionId) {
        registeredById.set(actionId, registeredAction)
      }
    }

    const currentPageData = findPageDataByFormId(currentRegistryFormId || currentSlotFormId)
    const rawActions = currentPageData?.form?.actions?.action
    const rawList = Array.isArray(rawActions) ? rawActions : rawActions ? [rawActions] : []
    for (const rawAction of rawList) {
      const hotKeyText = resolveActionHotKeyText(rawAction)
      const parsed = parseHotKey(hotKeyText)
      if (!parsed) continue

      const actionId = String(rawAction?._attributes?.id ?? rawAction?.id ?? '')
      const registered = actionId ? registeredById.get(actionId) : undefined
      const context = registered || {
        id: actionId || '0',
        name: String(rawAction?.name?._text ?? rawAction?.name ?? ''),
        formId: currentRegistryFormId || currentSlotFormId,
        panelId: '',
        elementId: '',
        pageId: '',
        level: String(rawAction?.layout?.level?._text ?? 'form').toLowerCase(),
        data: rawAction,
      }

      const key =
        actionId ||
        `${context?.level || ''}:${context?.elementId || context?.panelId || context?.pageId || context?.name || hotKeyText}`
      if (!candidatesAction.has(key)) {
        candidatesAction.set(key, { context, parsed })
      }
    }

    return Array.from(candidatesAction.values())
  }

  const executeRegisteredActionWithService = async (actionCtx: any): Promise<boolean> => {
    const actionData = actionCtx?.data
    if (!actionData?._attributes?.id) {
      return false
    }

    const layout = actionData?.layout || {}
    const layoutLevel = resolveActionLayoutLevelText(layout) || actionCtx.level || 'form'
    const layoutId = resolveActionLayoutTextId(layout)
    const pageId = layoutLevel === 'page' && layoutId ? layoutId : actionCtx.pageId
    const formId = String(
      actionCtx.formId ?? getCurrentRegistryFormId() ?? getCurrentFormId() ?? '',
    )

    if (!formId) {
      return false
    }

    const selectedData = actionCtx.selectedData ?? actionData.selected?.fields ?? null
    const actionPlatform =
      actionData.platform?._text ||
      currentPlatform.value ||
      findPageDataByFormId(formId)?.type ||
      undefined

    const actionContext = {
      id: String(actionCtx.id ?? actionData._attributes.id),
      name: actionCtx.name || actionData.name?._text || '',
      elementId:
        layoutLevel === 'element' && layoutId
          ? String(layoutId)
          : actionCtx.elementId != null
            ? String(actionCtx.elementId)
            : undefined,
      panelId: actionCtx.panelId != null ? String(actionCtx.panelId) : undefined,
      formId: formId || undefined,
      pageId: pageId != null ? String(pageId) : undefined,
      level: (layoutLevel || actionCtx.level || 'form') as
        | 'form'
        | 'panel'
        | 'element'
        | 'page',
      data: actionData,
      platform: actionPlatform,
      windowType: extractActionTextValue(actionData.window__type) as string | undefined,
      onClick: extractActionScriptText(actionData.onClick) || undefined,
      confirm: extractActionScriptText(actionData.confirm) || undefined,
      transfer: extractActionTextValue(actionData.transfer) as string | undefined,
      transferType: extractActionTextValue(actionData.transferType) as string | undefined,
      event: {
        dialogClosed: getActionDialogClosedScript(actionData as Record<string, any>),
        chainCalled: extractActionScriptText(actionData.event?.chainCalled) || undefined,
      },
      flow: extractActionScriptText(actionData.flow) || undefined,
      selectedData,
    }

    const { actionService } = await import('@/services/action/action.service')
    const depsPageData =
      findPageDataByFormId(formId) ||
      appStore.pagesData?.[formId] ||
      appStore.pagesData?.[String(appStore.currentPageId ?? '')]

    const topDialogId = appStore.dialogIds?.length
      ? String(appStore.dialogIds[appStore.dialogIds.length - 1] ?? '')
      : ''
    const pageStoreKey = resolvePagesDataStoreKey(appStore.pagesData as Record<string, any>, {
      pageDataRef: depsPageData,
      pageDataSlot: String(getCurrentFormId() || formId || ''),
      registryFormId: String(getCurrentRegistryFormId() || formId || ''),
      formId,
      currentPageId: appStore.currentPageId,
      topDialogId,
      windowInstanceId: String(getCurrentFormId() || formId || ''),
    })
    const slotId = pageStoreKey || String(getCurrentFormId() || formId || '')

    await actionService.executeAction(actionContext, {
      appStore,
      router,
      actions: tempActions,
      getForm,
      getPageData,
      getTableSelection,
      getTableSelections,
      formStates,
      pageData: depsPageData,
      pageDataSlot: slotId,
      pageRegistryFormId: String(getCurrentRegistryFormId() || formId || ''),
      pageWindowInstanceId: slotId,
      runCode,
      formProvider,
      reloadElement: reloadElementViaControls,
    })

    return true
  }

  const executeHotKeyCandidate = async (candidate: HotKeyCandidate): Promise<void> => {
    const executedViaService = await executeRegisteredActionWithService(candidate.context)
    if (executedViaService) return

    await onAction({ getContext: () => candidate.context })
  }

  const handleHotKey = (event: KeyboardEvent) => {
    if (event.defaultPrevented || event.isComposing || event.repeat) return
    const eventKey = normalizeKeyboardEventKey(event.key)
    if (!eventKey) return

    const isPlainTypingKey = eventKey.length === 1 || eventKey === 'space'
    if (!event.ctrlKey && !event.altKey && !event.metaKey && isEditableTarget(event.target) && isPlainTypingKey) {
      return
    }

    const candidates = collectHotKeyCandidates()
    const matched = candidates.find((candidate) => doesHotKeyMatch(event, candidate.parsed))
    if (!matched) return

    event.preventDefault()
    event.stopPropagation()
    void executeHotKeyCandidate(matched)
  }

  const attachHotKeys = () => {
    window.addEventListener('keydown', handleHotKey, true)
  }

  const detachHotKeys = () => {
    window.removeEventListener('keydown', handleHotKey, true)
  }

  return {
    attachHotKeys,
    detachHotKeys,
    handleHotKey,
  }
}
